/**
 * Admin Comparison Manager - CMS page for managing study abroad comparisons
 * SEO landing pages for keywords like "MS USA vs UK", "MBA vs MS"
 */
import { useState, useEffect } from 'react';
import { useAdminTheme } from '@/context/ThemeContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import PublishStatusCard from '@/components/admin/PublishStatusCard';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { AdminLayout } from '@/components/admin/AdminLayout';
import {
  Scale,
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  Loader2,
  Search,
  ExternalLink,
  RefreshCw,
  Star,
  StarOff
} from 'lucide-react';

const API_URL = window.location.origin || '';

const COMPARISON_TYPES = [
  { value: 'destination', label: 'Destination vs Destination' },
  { value: 'program', label: 'Program vs Program' },
  { value: 'degree', label: 'Degree vs Degree' }
];

const AdminComparisonManager = () => {
  const { isDark } = useAdminTheme();
  const [comparisons, setComparisons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingComparison, setEditingComparison] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    comparison_type: 'destination',
    tagline: '',
    description: '',
    keywords: '',
    introduction: '',
    items: [],
    comparison_factors: [],
    verdict: '',
    faq: [],
    is_featured: false,
    is_active: true
  });

  // Fetch comparisons
  const fetchComparisons = async () => {
    try {
      const res = await fetch(`${API_URL}/api/comparisons/admin/all`);
      if (res.ok) {
        const data = await res.json();
        setComparisons(data);
      }
    } catch (error) {
      console.error('Error fetching comparisons:', error);
      toast.error('Failed to fetch comparisons');
    } finally {
      setLoading(false);
    }
  };

  // Seed initial data
  const seedComparisons = async () => {
    try {
      const res = await fetch(`${API_URL}/api/comparisons/admin/seed`, { method: 'POST' });
      const data = await res.json();
      toast.success(data.message);
      fetchComparisons();
    } catch (error) {
      toast.error('Failed to seed comparisons');
    }
  };

  useEffect(() => {
    fetchComparisons();
  }, []);

  // Generate slug from title
  const generateSlug = (title) => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  };

  // Handle form input change
  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      if (field === 'title') {
        newData.slug = generateSlug(value);
      }
      return newData;
    });
  };

  // Handle comparison item changes
  const handleItemChange = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      return { ...prev, items: newItems };
    });
  };

  // Add new item
  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        name: '',
        duration: '',
        avg_cost: '',
        top_universities: [],
        work_visa: '',
        pr_pathway: '',
        avg_salary: '',
        pros: [],
        cons: [],
        best_for: ''
      }]
    }));
  };

  // Remove item
  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Handle FAQ changes
  const handleFaqChange = (index, field, value) => {
    setFormData(prev => {
      const newFaq = [...prev.faq];
      newFaq[index] = { ...newFaq[index], [field]: value };
      return { ...prev, faq: newFaq };
    });
  };

  // Add new FAQ
  const addFaq = () => {
    setFormData(prev => ({
      ...prev,
      faq: [...prev.faq, { question: '', answer: '' }]
    }));
  };

  // Remove FAQ
  const removeFaq = (index) => {
    setFormData(prev => ({
      ...prev,
      faq: prev.faq.filter((_, i) => i !== index)
    }));
  };

  // Open dialog for new comparison
  const handleAddNew = () => {
    setEditingComparison(null);
    setFormData({
      title: '',
      slug: '',
      comparison_type: 'destination',
      tagline: '',
      description: '',
      keywords: '',
      introduction: '',
      items: [],
      comparison_factors: [],
      verdict: '',
      faq: [],
      is_featured: false,
      is_active: true
    });
    setActiveTab('basic');
    setIsDialogOpen(true);
  };

  // Open dialog for editing
  const handleEdit = (comparison) => {
    setEditingComparison(comparison);
    setFormData({
      title: comparison.title || '',
      slug: comparison.slug || '',
      comparison_type: comparison.comparison_type || 'destination',
      tagline: comparison.tagline || '',
      description: comparison.description || '',
      keywords: comparison.keywords || '',
      introduction: comparison.introduction || '',
      items: comparison.items || [],
      comparison_factors: comparison.comparison_factors || [],
      verdict: comparison.verdict || '',
      faq: comparison.faq || [],
      is_featured: comparison.is_featured || false,
      is_active: comparison.is_active !== false
    });
    setActiveTab('basic');
    setIsDialogOpen(true);
  };

  // Save comparison
  const handleSave = async () => {
    if (!formData.title || !formData.slug) {
      toast.error('Title and Slug are required');
      return;
    }

    setSaving(true);
    try {
      const url = editingComparison
        ? `${API_URL}/api/comparisons/admin/${editingComparison.id}`
        : `${API_URL}/api/comparisons/admin`;
      
      const res = await fetch(url, {
        method: editingComparison ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success(editingComparison ? 'Comparison updated' : 'Comparison created');
        setIsDialogOpen(false);
        fetchComparisons();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save comparison');
      }
    } catch (error) {
      toast.error('Failed to save comparison');
    } finally {
      setSaving(false);
    }
  };

  // Toggle active status
  const handleToggleActive = async (comparison) => {
    try {
      const res = await fetch(`${API_URL}/api/comparisons/admin/${comparison.id}/toggle-active`, { method: 'POST' });
      if (res.ok) {
        toast.success(`Comparison ${comparison.is_active ? 'deactivated' : 'activated'}`);
        fetchComparisons();
      }
    } catch (error) {
      toast.error('Failed to toggle status');
    }
  };

  // Toggle featured status
  const handleToggleFeatured = async (comparison) => {
    try {
      const res = await fetch(`${API_URL}/api/comparisons/admin/${comparison.id}/toggle-featured`, { method: 'POST' });
      if (res.ok) {
        toast.success(`Comparison ${comparison.is_featured ? 'unfeatured' : 'featured'}`);
        fetchComparisons();
      }
    } catch (error) {
      toast.error('Failed to toggle featured');
    }
  };

  // Delete comparison
  const handleDelete = async (comparison) => {
    if (!window.confirm(`Are you sure you want to delete "${comparison.title}"?`)) return;

    try {
      const res = await fetch(`${API_URL}/api/comparisons/admin/${comparison.id}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Comparison deleted');
        fetchComparisons();
      }
    } catch (error) {
      toast.error('Failed to delete comparison');
    }
  };

  // Filter comparisons
  const filteredComparisons = comparisons.filter(comp => {
    const matchesSearch = comp.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || comp.comparison_type === filterType;
    return matchesSearch && matchesType;
  });

  const TabButton = ({ tab, label }) => (
    <button
      onClick={() => setActiveTab(tab)}
      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
        activeTab === tab
          ? isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-700'
          : isDark ? 'text-white/60 hover:text-white' : 'text-slate-600 hover:text-slate-900'
      }`}
    >
      {label}
    </button>
  );

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-comparison-manager">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Comparison Manager
          </h1>
          <p className={`text-sm ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
            Manage SEO comparison pages (USA vs UK, MBA vs MS, etc.)
          </p>
        </div>
        <div className="flex gap-2">
          {comparisons.length === 0 && (
            <Button variant="outline" onClick={seedComparisons} data-testid="seed-comparisons-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Seed Initial Data
            </Button>
          )}
          <Button onClick={handleAddNew} className="bg-gradient-to-r from-purple-500 to-blue-500 text-white" data-testid="add-comparison-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Comparison
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-white/40' : 'text-slate-400'}`} />
          <Input
            placeholder="Search comparisons..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`pl-10 ${isDark ? 'bg-white/5 border-white/10' : ''}`}
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className={`w-[200px] ${isDark ? 'bg-white/5 border-white/10' : ''}`}>
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {COMPARISON_TYPES.map(t => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Comparisons Table */}
      <Card className={isDark ? 'bg-white/5 border-white/10' : ''}>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
            </div>
          ) : filteredComparisons.length === 0 ? (
            <div className="text-center py-12">
              <Scale className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-white/20' : 'text-slate-300'}`} />
              <p className={isDark ? 'text-white/60' : 'text-slate-500'}>
                {searchTerm || filterType !== 'all' ? 'No comparisons found' : 'No comparisons yet. Click "Seed Initial Data" to add default comparisons.'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className={isDark ? 'border-white/10' : ''}>
                  <TableHead className={isDark ? 'text-white/70' : ''}>Comparison</TableHead>
                  <TableHead className={isDark ? 'text-white/70' : ''}>Type</TableHead>
                  <TableHead className={isDark ? 'text-white/70' : ''}>Items</TableHead>
                  <TableHead className={isDark ? 'text-white/70' : ''}>Status</TableHead>
                  <TableHead className={`text-right ${isDark ? 'text-white/70' : ''}`}>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredComparisons.map((comparison) => (
                  <TableRow key={comparison.id} className={isDark ? 'border-white/10' : ''}>
                    <TableCell className={isDark ? 'text-white' : ''}>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{comparison.title}</span>
                          {comparison.is_featured && <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />}
                        </div>
                        <div className={`text-xs mt-1 ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
                          /compare/{comparison.slug}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={isDark ? 'border-white/30 text-white/80' : ''}>
                        {COMPARISON_TYPES.find(t => t.value === comparison.comparison_type)?.label || comparison.comparison_type}
                      </Badge>
                    </TableCell>
                    <TableCell className={isDark ? 'text-white/80' : ''}>
                      {comparison.items?.length || 0} compared
                    </TableCell>
                    <TableCell>
                      <Badge variant={comparison.is_active ? 'default' : 'secondary'}>
                        {comparison.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => window.open(`/compare/${comparison.slug}`, '_blank')} title="Preview">
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleToggleFeatured(comparison)} title={comparison.is_featured ? 'Unfeature' : 'Feature'}>
                          {comparison.is_featured ? <StarOff className="w-4 h-4 text-yellow-500" /> : <Star className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleToggleActive(comparison)} title={comparison.is_active ? 'Deactivate' : 'Activate'}>
                          {comparison.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(comparison)} title="Edit">
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(comparison)} className="text-red-500 hover:text-red-600" title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit/Create Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className={`max-w-4xl max-h-[90vh] overflow-y-auto ${isDark ? 'bg-[#1a2830] border-white/10' : ''}`}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''}>
              {editingComparison ? 'Edit Comparison' : 'Add New Comparison'}
            </DialogTitle>
            <DialogDescription className={isDark ? 'text-white/60' : ''}>
              {editingComparison ? 'Update comparison details' : 'Create a new SEO comparison page'}
            </DialogDescription>
          </DialogHeader>

          {/* Tabs */}
          <div className="flex gap-2 border-b pb-2 mb-4 overflow-x-auto">
            <TabButton tab="basic" label="Basic Info" />
            <TabButton tab="seo" label="SEO Content" />
            <TabButton tab="items" label="Comparison Items" />
            <TabButton tab="faq" label="FAQ" />
          </div>

          <div className="space-y-6 py-4">
            {/* Basic Info Tab */}
            {activeTab === 'basic' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className={isDark ? 'text-white' : ''}>Title *</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      placeholder="e.g., MS in USA vs UK vs Canada"
                      className={isDark ? 'bg-white/5 border-white/10' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className={isDark ? 'text-white' : ''}>Comparison Type</Label>
                    <Select value={formData.comparison_type} onValueChange={(v) => handleInputChange('comparison_type', v)}>
                      <SelectTrigger className={isDark ? 'bg-white/5 border-white/10' : ''}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COMPARISON_TYPES.map(t => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className={isDark ? 'text-white' : ''}>URL Slug *</Label>
                  <Input
                    value={formData.slug}
                    onChange={(e) => handleInputChange('slug', e.target.value)}
                    placeholder="e.g., ms-usa-vs-uk-vs-canada"
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                  />
                  <p className={`text-xs ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
                    URL: /compare/{formData.slug || 'slug'}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className={isDark ? 'text-white' : ''}>Tagline</Label>
                  <Input
                    value={formData.tagline}
                    onChange={(e) => handleInputChange('tagline', e.target.value)}
                    placeholder="e.g., Which Country is Best for Your Master's Degree?"
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                  />
                </div>

                <PublishStatusCard
                  isPublished={formData.is_active}
                  onChange={(checked) => handleInputChange('is_active', checked)}
                  isDark={isDark}
                  entityLabel="comparison"
                  publicRoute="/compare"
                  testId="comparison-publish-toggle"
                />

                <div className="flex items-center gap-4">
                  <Switch checked={formData.is_featured} onCheckedChange={(checked) => handleInputChange('is_featured', checked)} />
                  <Label className={isDark ? 'text-white' : ''}>Featured</Label>
                </div>
              </>
            )}

            {/* SEO Content Tab */}
            {activeTab === 'seo' && (
              <>
                <div className="space-y-2">
                  <Label className={isDark ? 'text-white' : ''}>Meta Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="150-160 chars for search engines..."
                    rows={2}
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                  />
                  <p className={`text-xs ${isDark ? 'text-white/40' : 'text-slate-400'}`}>{formData.description.length}/160</p>
                </div>

                <div className="space-y-2">
                  <Label className={isDark ? 'text-white' : ''}>SEO Keywords</Label>
                  <Textarea
                    value={formData.keywords}
                    onChange={(e) => handleInputChange('keywords', e.target.value)}
                    placeholder="MS USA vs UK, best country for masters..."
                    rows={2}
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                  />
                </div>

                <div className="space-y-2">
                  <Label className={isDark ? 'text-white' : ''}>Introduction</Label>
                  <Textarea
                    value={formData.introduction}
                    onChange={(e) => handleInputChange('introduction', e.target.value)}
                    placeholder="Opening paragraph for the comparison..."
                    rows={4}
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                  />
                </div>

                <div className="space-y-2">
                  <Label className={isDark ? 'text-white' : ''}>Verdict / Conclusion</Label>
                  <Textarea
                    value={formData.verdict}
                    onChange={(e) => handleInputChange('verdict', e.target.value)}
                    placeholder="Final recommendation. Use **bold** for emphasis..."
                    rows={4}
                    className={isDark ? 'bg-white/5 border-white/10' : ''}
                  />
                </div>
              </>
            )}

            {/* Comparison Items Tab */}
            {activeTab === 'items' && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <Label className={isDark ? 'text-white' : ''}>Comparison Items ({formData.items.length})</Label>
                  <Button variant="outline" size="sm" onClick={addItem}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Item
                  </Button>
                </div>

                {formData.items.map((item, idx) => (
                  <Card key={idx} className={`mb-4 ${isDark ? 'bg-white/5 border-white/10' : ''}`}>
                    <CardContent className="p-4 space-y-4">
                      <div className="flex justify-between items-center">
                        <Label className={`font-bold ${isDark ? 'text-white' : ''}`}>Item {idx + 1}</Label>
                        <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} className="text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className={isDark ? 'text-white/80' : ''}>Name *</Label>
                          <Input
                            value={item.name || ''}
                            onChange={(e) => handleItemChange(idx, 'name', e.target.value)}
                            placeholder="e.g., MS in USA"
                            className={isDark ? 'bg-white/10 border-white/20' : ''}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={isDark ? 'text-white/80' : ''}>Duration</Label>
                          <Input
                            value={item.duration || ''}
                            onChange={(e) => handleItemChange(idx, 'duration', e.target.value)}
                            placeholder="e.g., 1.5-2 years"
                            className={isDark ? 'bg-white/10 border-white/20' : ''}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={isDark ? 'text-white/80' : ''}>Average Cost</Label>
                          <Input
                            value={item.avg_cost || ''}
                            onChange={(e) => handleItemChange(idx, 'avg_cost', e.target.value)}
                            placeholder="e.g., $50,000-80,000/year"
                            className={isDark ? 'bg-white/10 border-white/20' : ''}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={isDark ? 'text-white/80' : ''}>Average Salary</Label>
                          <Input
                            value={item.avg_salary || ''}
                            onChange={(e) => handleItemChange(idx, 'avg_salary', e.target.value)}
                            placeholder="e.g., $100,000-150,000/year"
                            className={isDark ? 'bg-white/10 border-white/20' : ''}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={isDark ? 'text-white/80' : ''}>Work Visa</Label>
                          <Input
                            value={item.work_visa || ''}
                            onChange={(e) => handleItemChange(idx, 'work_visa', e.target.value)}
                            placeholder="e.g., 3-year STEM OPT"
                            className={isDark ? 'bg-white/10 border-white/20' : ''}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={isDark ? 'text-white/80' : ''}>PR Pathway</Label>
                          <Input
                            value={item.pr_pathway || ''}
                            onChange={(e) => handleItemChange(idx, 'pr_pathway', e.target.value)}
                            placeholder="e.g., H-1B → Green Card"
                            className={isDark ? 'bg-white/10 border-white/20' : ''}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className={isDark ? 'text-white/80' : ''}>Top Universities (comma separated)</Label>
                        <Input
                          value={(item.top_universities || []).join(', ')}
                          onChange={(e) => handleItemChange(idx, 'top_universities', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                          placeholder="MIT, Stanford, CMU"
                          className={isDark ? 'bg-white/10 border-white/20' : ''}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className={isDark ? 'text-white/80' : ''}>Pros (one per line)</Label>
                          <Textarea
                            value={(item.pros || []).join('\n')}
                            onChange={(e) => handleItemChange(idx, 'pros', e.target.value.split('\n').filter(Boolean))}
                            placeholder="Highest salaries&#10;Best universities"
                            rows={4}
                            className={isDark ? 'bg-white/10 border-white/20' : ''}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={isDark ? 'text-white/80' : ''}>Cons (one per line)</Label>
                          <Textarea
                            value={(item.cons || []).join('\n')}
                            onChange={(e) => handleItemChange(idx, 'cons', e.target.value.split('\n').filter(Boolean))}
                            placeholder="High cost&#10;Visa uncertainty"
                            rows={4}
                            className={isDark ? 'bg-white/10 border-white/20' : ''}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className={isDark ? 'text-white/80' : ''}>Best For</Label>
                        <Input
                          value={item.best_for || ''}
                          onChange={(e) => handleItemChange(idx, 'best_for', e.target.value)}
                          placeholder="e.g., Students targeting highest ROI and tech careers"
                          className={isDark ? 'bg-white/10 border-white/20' : ''}
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </>
            )}

            {/* FAQ Tab */}
            {activeTab === 'faq' && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <Label className={isDark ? 'text-white' : ''}>FAQ ({formData.faq.length})</Label>
                  <Button variant="outline" size="sm" onClick={addFaq}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add FAQ
                  </Button>
                </div>

                {formData.faq.map((item, idx) => (
                  <Card key={idx} className={`mb-4 ${isDark ? 'bg-white/5 border-white/10' : ''}`}>
                    <CardContent className="p-4 space-y-4">
                      <div className="flex justify-between items-center">
                        <Label className={`font-bold ${isDark ? 'text-white' : ''}`}>FAQ {idx + 1}</Label>
                        <Button variant="ghost" size="sm" onClick={() => removeFaq(idx)} className="text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <Label className={isDark ? 'text-white/80' : ''}>Question</Label>
                        <Input
                          value={item.question || ''}
                          onChange={(e) => handleFaqChange(idx, 'question', e.target.value)}
                          className={isDark ? 'bg-white/10 border-white/20' : ''}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className={isDark ? 'text-white/80' : ''}>Answer</Label>
                        <Textarea
                          value={item.answer || ''}
                          onChange={(e) => handleFaqChange(idx, 'answer', e.target.value)}
                          rows={3}
                          className={isDark ? 'bg-white/10 border-white/20' : ''}
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-gradient-to-r from-purple-500 to-blue-500 text-white">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingComparison ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminComparisonManager;
